// React
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
// ライブラリ
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
// カスタムフック
import { useEditStateStorage } from '../../../hooks/useEditStateStorage';
// ユーティリティ
import { validationSchema } from '../../../utils/validationUtils';
import { successMessages, errorMessages } from '../../../utils/messagesUtils';
import { menu } from '../../../constants';
// スタイル
import '../../../lib/ReactModalStyle.css';
import sty from './Main.module.css';
// ステート
import { skillsheetState } from './state/skillsheetState';

const defaultSkillsheet = [{
        input:{
            skillsheet_id: 0,
            skillsheet_name: '',
            career_overview: '',
            speciality: '',
            future_direction: '',
            is_public: 'false',
            project: {
                project_data:[]
            }
        },
        output:{
            random1Id: '',
            random2Id: '',
            created_at: '',
            updated_at: ''  
        }
    }];


const addFirstNewForm = () =>{
    skillsheetState.forms = [...defaultSkillsheet];
    skillsheetState.triggerChange();
};

// 実際のコンポーネント
const Skillsheet = () => {
    return <MySkillsheet />;
};

const MySkillsheet = () => {
    // フックの初期化
    const navigate = useNavigate();
    const { register, setValue, formState: { errors }, trigger } = useForm({
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        defaultValues: defaultSkillsheet,
    });
    const [isLoading, setIsLoading] = useState(true);

    // 編集状態管理用のステート
    const [originalData, setOriginalData] = useState(null);
    
    // モーダルウィンドウの状態管理
    const [modalOpen, setModalOpen] = useState(false);
    const [visibleStates, setVisibleStates] = useState([]);
    const [charCounts, setCharCounts] = useState({
        career_overview: {},
        speciality: {},
        future_direction: {}
    });

    // カスタムフックの使用
    const { checkForEditData, clearStoredData } = useEditStateStorage(
        'skillsheet', // ストレージキープレフィックス
        () => skillsheetState.forms,
        (data) => {
            if (Array.isArray(data) && data.length > 0) {
                // データが配列の形式で保存されている場合、それをformsにセット
                skillsheetState.forms = data;

                // 表示状態をセット
                setVisibleStates(Array(data.length).fill(true));
            }
        },
        originalData, // オリジナルデータ
    );
    
    // 内部関数の定義
    const readProjectDataFromDB = () => {
        return new Promise((resolve, reject)=>{
            fetch(`${process.env.REACT_APP_API_BASE_URL}/project`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include'
            }).then(async (response) =>{
                const result = await response.json();
                if(response.status === 200 && result.status === 'OK'){
                    const data = result?.data
                    let project_master_data = [];
                    for(let box = 0; box < data.projectsBox.length; box++){
                        project_master_data.push({
                            'project_id' : data.projectsBox[box].project_id,
                            'project_name' : data.projectsBox[box].project_name,
                        });  
                    }
                    resolve(project_master_data);
                } else {
                    if(response.status === 401){
                        navigate(menu.ENGINEER.SIGNIN.PATH)
                    } else if(response.status === 404) {
                        const error = new Error();
                        error.front_message = errorMessages.common.data_not_found;
                        throw error;
                    } else {
                        const error = new Error();
                        error.front_message = errorMessages.common.data_fetch_failed;
                        throw error;
                    }
                }
            }).catch((e) =>{
                Swal.fire({
                    icon: 'error',
                    html: `${e.front_message || errorMessages.api.server_error}`,
                    customClass: {
                        htmlContainer: 'large-text'
                    }
                })
                reject();
            });
        });
    };

    const readSkillsheetDataFromDB = (project_master_data) => {
        setIsLoading(true);
        return new Promise((resolve, reject)=>{
            fetch(`${process.env.REACT_APP_API_BASE_URL}/skillsheet`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include'
            }).then(async (response) =>{
                const result = await response.json();
                if (response.status === 200 && result.status === 'OK') {
                    const skillsheetBox = result.skillsheetBox;
                    skillsheetState.forms = skillsheetBox;
                    setOriginalData(skillsheetBox);
                    checkForEditData(skillsheetBox);
                    return Promise.resolve(skillsheetBox);
                } else {
                    if(response.status === 401){
                        navigate(menu.ENGINEER.SIGNIN.PATH)
                    } else if(response.status === 404) {
                        const error = new Error();
                        error.front_message = errorMessages.common.data_not_found;
                        throw error;
                    } else {
                        const error = new Error();
                        error.front_message = errorMessages.common.data_fetch_failed;
                        throw error;
                    }
                }      
            }).then((skillsheetBox)=>{
                let _project_master_data = [];            
                for(let pmd = 0; pmd < project_master_data.length; pmd++){
                    _project_master_data.push({
                        project_id: project_master_data[pmd].project_id,
                        project_name: project_master_data[pmd].project_name
                    });
                }
                skillsheetState.project_master_data = _project_master_data;
                
                if(skillsheetState.project_master_data.length > 0){
                    skillsheetState.triggerChange();
                }
                
                let forms = [];
                for(let box = 0; box < skillsheetBox.length; box++){                    
                    forms.push(skillsheetBox[box].skillsheet);
                }
                skillsheetState.forms = forms;
                setOriginalData(JSON.parse(JSON.stringify(forms)));
                
                resolve();
            }).catch((e)=>{
                Swal.fire({
                    icon: 'error',
                    html: `${e.front_message || errorMessages.api.server_error}`,
                    customClass: {
                        htmlContainer: 'large-text'
                    }
                })
                reject();
            }).finally(() => {
                setIsLoading(false);
            });
        });
    };
    
    const setSkillSheetName = (e, skillsheet_id) => {
        for(let box = 0; box < skillsheetState.forms.length; box++){
            if(skillsheetState.forms[box].input.skillsheet_id === skillsheet_id){
                skillsheetState.forms[box].input.skillsheet_name = e.target.value;
                skillsheetState.triggerChange();
                break;
            }
        }
    };
    
    const setPublic = (e, skillsheet_id) => {
        for(let box = 0; box < skillsheetState.forms.length; box++){
            if(skillsheetState.forms[box].input.skillsheet_id === skillsheet_id){
                skillsheetState.forms[box].input.is_public = e.target.value;
                skillsheetState.triggerChange();
                break;
            }
        }
    };
    
    const setCareerOverview = (e, skillsheet_id) => {
        const value = e.target.value;
        for(let box = 0; box < skillsheetState.forms.length; box++){
            if(skillsheetState.forms[box].input.skillsheet_id === skillsheet_id){
                skillsheetState.forms[box].input.career_overview = e.target.value;
                // 文字数の更新
                setCharCounts(prev => ({
                    ...prev,
                    career_overview: {
                        ...prev.career_overview,
                        [skillsheet_id]: value.length
                    }
                }));
                skillsheetState.triggerChange();
                break;
            }
        }
    };
    
    const setSpeciality = (e, skillsheet_id) => {
        const value = e.target.value;
        for(let box = 0; box < skillsheetState.forms.length; box++){
            if(skillsheetState.forms[box].input.skillsheet_id === skillsheet_id){
                skillsheetState.forms[box].input.speciality = e.target.value;
                // 文字数の更新
                setCharCounts(prev => ({
                    ...prev,
                    speciality: {
                        ...prev.speciality,
                        [skillsheet_id]: value.length
                    }
                }));
                skillsheetState.triggerChange();
                break;
            }
        }
    };
    
    const setFutureDirection = (e, skillsheet_id) => {
        const value = e.target.value;
        for(let box = 0; box < skillsheetState.forms.length; box++){
            if(skillsheetState.forms[box].input.skillsheet_id === skillsheet_id){
                skillsheetState.forms[box].input.future_direction = e.target.value;
                // 文字数の更新
                setCharCounts(prev => ({
                    ...prev,
                    future_direction: {
                        ...prev.future_direction,
                        [skillsheet_id]: value.length
                    }
                }));
                skillsheetState.triggerChange();
                break;
            }
        }
    };

    const handleProjectCheckboxChange = (e, skillsheet_id, project_id) => {
        const isChecked = e.target.checked;
        const projectName = e.target.nextElementSibling.textContent;
        
        for(let box = 0; box < skillsheetState.forms.length; box++) {
            if(skillsheetState.forms[box].input.skillsheet_id === skillsheet_id) {
                // 既存のプロジェクトデータ配列を取得
                let projectData = [...skillsheetState.forms[box].input.project.project_data];
                
                if (isChecked) {
                    // チェックが入った場合、プロジェクトを追加
                    projectData.push({
                        project_id: project_id,
                        selected_project_name: projectName
                    });
                } else {
                    // チェックが外れた場合、プロジェクトを削除
                    projectData = projectData.filter(item => item.project_id !== project_id);
                }
                
                // 更新されたプロジェクトデータをセット
                skillsheetState.forms[box].input.project.project_data = projectData;
                skillsheetState.triggerChange();
                break;
            }
        }
    };
    
    const deleteForm = async (formIndex, skillsheet_id) => {
        const result = await Swal.fire({
            icon: 'warning',
            html: `本当に <b>スキルシート${formIndex + 1}</b><br>を削除してもよろしいですか?`,
            showCancelButton: true,
            confirmButtonText: '削除',
            cancelButtonText: 'キャンセル',
            customClass: {
                htmlContainer: 'large-text'
            }
        });
        
        if (result.isConfirmed) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/skillsheet/${skillsheet_id}`, {
                    method: 'DELETE',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include'
                });
                
                const result = await response.json();
                if (response.status === 200 && result.status === 'OK') {
                    // ローカルストレージのデータをクリア
                    clearStoredData();
                    
                    for(let box = 0; box < skillsheetState.forms.length; box++){
                        if(skillsheetState.forms[box].input.skillsheet_id === skillsheet_id){
                            skillsheetState.forms.splice(box,1);
                            break;
                        }
                    }
                    
                    // 状態を更新
                    setOriginalData(JSON.parse(JSON.stringify(skillsheetState.forms)));
                    
                    await Swal.fire({
                        icon: 'success',
                        html: `<b>スキルシート${formIndex + 1}</b> を削除しました。`,
                        customClass: {
                            htmlContainer: 'large-text'
                        }
                    });
                    skillsheetState.triggerChange();
                } else {         
                    if(response.status === 401){
                        navigate(menu.ENGINEER.SIGNIN.PATH)
                    } else {
                        await Swal.fire({ icon: 'error', title: errorMessages.skillsheets.delete_failed });
                        for(let box = 0; box < skillsheetState.forms.length; box++){
                            if(skillsheetState.forms[box].input.skillsheet_id === skillsheet_id){
                                skillsheetState.forms.splice(box,1);
                                skillsheetState.triggerChange();
                                break;
                            }
                        }
                    }
                }
            } catch (e) {
                await Swal.fire({
                    icon: 'error',
                    html: `${e.front_message || errorMessages.api.server_error}`,
                    customClass: {
                        htmlContainer: 'large-text'
                    }
                })
            }
        }
    };
    
    const submitFormHandler = async (skillsheet_id) => {
        const fieldsToValidate = [
            `skillsheet_name_${skillsheet_id}`,
            `career_overview_${skillsheet_id}`,
            `speciality_${skillsheet_id}`,
            `future_direction_${skillsheet_id}`,
        ];
        
        const isValid = await Promise.all(fieldsToValidate.map(field => trigger(field)));
        const allValid = isValid.every(result => result);
        
        if (!allValid) {
            // エラー要素へスクロール
            setTimeout(() => {
                const firstErrorField = Object.keys(errors).find(key => key.includes(`_${skillsheet_id}`));
                if (firstErrorField) {
                    const errorElement = document.getElementById(firstErrorField);
                    if (errorElement) {
                        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        errorElement.focus();
                    }
                }
            });
            return; // バリデーションエラーがある場合は処理を中断
        }

        let skillsheetGoingToInsert = null;
        for(let box = 0; box < skillsheetState.forms.length; box++){
            if(skillsheetState.forms[box].input.skillsheet_id === skillsheet_id){
                skillsheetGoingToInsert = skillsheetState.forms[box];
                break;
            }
        }
        
        //登録の処理
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/skillsheet`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ requestData: skillsheetGoingToInsert }),
                credentials: 'include'
            });
            
            const result = await response.json();
            if (response.status === 200 && result.status === 'OK') {
                const requestDataWithOutput = result.requestDataWithOutput;
                
                // ローカルストレージの一時保存データを削除
                clearStoredData();

                for(let box = 0; box < skillsheetState.forms.length; box++){
                    if(skillsheetState.forms[box].input.skillsheet_id === skillsheet_id){
                        skillsheetState.forms[box] = requestDataWithOutput;
                        break;
                    }
                }
                
                // 最新データで状態を更新
                setOriginalData(JSON.parse(JSON.stringify(skillsheetState.forms)));
                
                skillsheetState.triggerChange();
                await Swal.fire({ icon: 'success', title: successMessages.operation.created });
            } else {
                if(response.status === 401){
                    navigate(menu.ENGINEER.SIGNIN.PATH)
                } else {
                    throw new Error();
                }
            }
        } catch (e) {
            await Swal.fire({
                icon: 'error',
                html: `${e.front_message || errorMessages.api.server_error}`,
                customClass: {
                    htmlContainer: 'large-text'
                }
            });
        }
    };
    
    const copyUrlToClipboard = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            // コピー操作が成功したら、モーダルウィンドウを表示
            setModalOpen(true);
            // 3秒後にモーダルウィンドウを閉じる
            setTimeout(() => {
                setModalOpen(false);
            }, 1500);
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                html: `${errorMessages.skillsheets.copy_url_failed}`,
                customClass: {
                    htmlContainer: 'large-text'
                }
            });
        }
    };
    
    const toggleVisibility = (index) => {
        const newVisibleStates = {...visibleStates};
        newVisibleStates[index] = !newVisibleStates[index];
        setVisibleStates(newVisibleStates);
    };

    // データの初期読み込み
    useEffect(() => {
        readProjectDataFromDB()
            .then(async (projectBox) => {
                await readSkillsheetDataFromDB(projectBox);
                
                // データ読み込み後のローカルストレージ確認
                if (originalData) {
                    checkForEditData(originalData);
                }
            })
            .catch(() => {
                Swal.fire({
                    icon: 'error',
                    html: `${errorMessages.api.server_error}`,
                    customClass: {
                        htmlContainer: 'large-text'
                    }
                })
            });
    }, []);

    // フォーム値の設定
    useEffect(() => {
        if (skillsheetState.forms.length === 0) {
            setValue('skillsheet_name_0', '');
            setValue('career_overview_0', '');
            setValue('speciality_0', '');
            setValue('future_direction_0', '');
        } else {
            skillsheetState.forms.forEach((form, index) => {
                setValue(`skillsheet_name_${form.input.skillsheet_id}`, form.input.skillsheet_name);
                setValue(`career_overview_${form.input.skillsheet_id}`, form.input.career_overview);
                setValue(`speciality_${form.input.skillsheet_id}`, form.input.speciality);
                setValue(`future_direction_${form.input.skillsheet_id}`, form.input.future_direction);
            });
        }
    }, [skillsheetState.forms, setValue]);

    // 初期文字数カウント設定
    useEffect(() => {
        if (skillsheetState.forms.length > 0) {
            const initialCareerOverview = {};
            const initialSpeciality = {};
            const initialFutureDirection = {};
            
            skillsheetState.forms.forEach(form => {
                const id = form.input.skillsheet_id;
                initialCareerOverview[id] = form.input.career_overview?.length || 0;
                initialSpeciality[id] = form.input.speciality?.length || 0;
                initialFutureDirection[id] = form.input.future_direction?.length || 0;
            });
            
            setCharCounts({
                career_overview: initialCareerOverview,
                speciality: initialSpeciality,
                future_direction: initialFutureDirection
            });
        }
    }, [skillsheetState.forms]);

    // グローバル状態を監視して再レンダリングを発火
    skillsheetState.hook('forms');

    return (
        <>
            {isLoading && <div style={{ textAlign: 'center', padding: '40px 0', fontSize: '1.2em' }}>Loading...</div>}
            
            {!isLoading && skillsheetState.forms.length === 0 && (
                <div className={sty['form']} key="new" onClick={(e) => addFirstNewForm()} style={{cursor: 'pointer'}}>
                    <div className={`${sty['form-tab__content-inner']} ${sty['form-tab__content-inner-pt-minus']}`} key="0">{/* フォームが表示されていないときだけ toggleVisibility を実行 */}
                        <div>
                            <div style={{textAlign: 'center', color:'darkgray'}}>＋新規作成</div>
                        </div>
                    </div>    
                </div>
            )}
            {!isLoading && skillsheetState.forms.map((form, formIndex) => (
                <div className={sty['form']} key={form.input.skillsheet_id} id={`skillsheet_panel_${form.input.skillsheet_id}`}>
                    <div className={`${sty['form-tab__content-inner']} ${sty['form-tab__content-inner-pt-minus']}`} key={form.input.skillsheet_id}>{/* フォームが表示されていないときだけ toggleVisibility を実行 */}
                        <div className={sty['form__tit-flex']}>
                            <div className={sty['form__tit-inner']}>
                                <div className={sty['form__no']}>{formIndex + 1}</div>
                                <div className={sty['form__tit-flex-inner']}>
                                    <h2 className={`${sty['form__tit']} ${sty['form__tit-pj']}`} id={`skillsheet_name_header_${form.input.skillsheet_id}`}>{form.input.skillsheet_name || `スキルシート${formIndex+1}`}</h2>
                                    {form.output.updated_at !== '' &&( 
                                        <div className={sty['form__flex-paragraphs']}>
                                            <p className={sty['form__updated-date']}><img src="/images/update_24.svg" width="16" height="16"/> {dayjs(form.output.updated_at)?.format("YYYY/MM/DD HH:mm")}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <button type="button" onClick={() => toggleVisibility(form.input.skillsheet_id)} className={sty['show-more-less']}>
                                {visibleStates[form.input.skillsheet_id] ? '▲' : '▼'}
                            </button>
                        </div>

                        {visibleStates[form.input.skillsheet_id] && <div className={sty['form__tit-line']}></div>}

                        {visibleStates[form.input.skillsheet_id] && (<div className={sty['form__inputs-outer']}>
                            <div className={sty['form__flex']}>
                                <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                                    <div className={sty['form__label']}>スキルシート名</div>
                                    <div className={sty['required']}>必須</div>
                                </div>
                                <div className={sty['form__input']}>                                    
                                    <input {...register(`skillsheet_name_${form.input.skillsheet_id}`, validationSchema.skillsheet_name)} id={`skillsheet_name_${form.input.skillsheet_id}`}
                                        type="text"
                                        defaultValue={form.input.skillsheet_name}
                                        placeholder="バックエンド案件向けのスキルシート"
                                        onChange={(e) => setSkillSheetName(e, form.input.skillsheet_id)}
                                    />
                                    {errors[`skillsheet_name_${form.input.skillsheet_id}`] && <p className="form__validation-msg">{errors[`skillsheet_name_${form.input.skillsheet_id}`].message}</p>}
                                </div>
                            </div>

                            <div className={sty['form__flex']}>
                                <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                                    <div className={sty['form__label']}>経歴概要</div>
                                    <div className={sty['required']}>必須</div>
                                </div>
                                <div className={sty['form__input']}>
                                    <div className={sty['textarea-wrapper']}>
                                        <textarea  {...register(`career_overview_${form.input.skillsheet_id}`, validationSchema.career_overview)} id={`career_overview_${form.input.skillsheet_id}`}
                                            defaultValue={form.input.career_overview}
                                            rows="7"
                                            placeholder={`Oracleデータベースの管理・運用を主軸として、DB設計（物理／論理）・構築、パフォーマンスチューニングなど、DBAとして幅広く実務を経験。
開発案件も並行して上流から下流工程まで継続的に対応。`} onChange={(e) => setCareerOverview(e, form.input.skillsheet_id)}>
                                        </textarea>
                                        <div className={`${sty['char-count']} ${charCounts.career_overview[form.input.skillsheet_id] > 900 ? sty['warning'] : ''} ${charCounts.career_overview[form.input.skillsheet_id] > 1000 ? sty['exceeded'] : ''}`}>
                                            {`${charCounts.career_overview[form.input.skillsheet_id] || 0} / 1000`}
                                        </div>
                                    </div>
                                    {errors[`career_overview_${form.input.skillsheet_id}`] && <p className="form__validation-msg">{errors[`career_overview_${form.input.skillsheet_id}`].message}</p>}
                                </div>
                            </div>

                            <div className={sty['form__flex']}>
                                <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                                    <div className={sty['form__label']}>得意分野</div>
                                    <div className={sty['required']}>必須</div>
                                </div>
                                <div className={sty['form__input']}>
                                    <div className={sty['textarea-wrapper']}>
                                        <textarea {...register(`speciality_${form.input.skillsheet_id}`, validationSchema.speciality)} id={`speciality_${form.input.skillsheet_id}`}
                                            defaultValue={form.input.speciality}
                                            rows="7"
                                            placeholder={`・Oracleデータベースの管理・運用
・DB設計（物理／論理）、DB構築
・パフォーマンスチューニング`} onChange={(e) => setSpeciality(e, form.input.skillsheet_id)}>
                                        </textarea>
                                        <div className={`${sty['char-count']} ${charCounts.speciality[form.input.skillsheet_id] > 400 ? sty['warning'] : ''} ${charCounts.speciality[form.input.skillsheet_id] > 500 ? sty['exceeded'] : ''}`}>
                                            {`${charCounts.speciality[form.input.skillsheet_id] || 0} / 500`}
                                        </div>
                                    </div>
                                    {errors[`speciality_${form.input.skillsheet_id}`] && <p className="form__validation-msg">{errors[`speciality_${form.input.skillsheet_id}`].message}</p>}
                                </div>
                            </div>

                            <div className={sty['form__flex']}>
                                <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                                    <div className={sty['form__label']}>今後の方向性</div>
                                    <div className={sty['required']}>必須</div>
                                </div>
                                <div className={sty['form__input']}>
                                    <div className={sty['textarea-wrapper']}>
                                        <textarea {...register(`future_direction_${form.input.skillsheet_id}`, validationSchema.future_direction)} id={`future_direction_${form.input.skillsheet_id}`}
                                            defaultValue={form.input.future_direction}
                                            rows="7"
                                            placeholder={`これまで培った能力を生かし、ITを使用した業務レベルの改善提案、およびシステム導入の計画・支援などのコンサルティング業務を多く経験して行きたい。
クラウドや自動化、DevOps、アジャイル開発など新しい技術や概念も積極的に取り入れていきたい。`} onChange={(e) => setFutureDirection(e, form.input.skillsheet_id)}>
                                        </textarea>
                                        <div className={`${sty['char-count']} ${charCounts.future_direction[form.input.skillsheet_id] > 900 ? sty['warning'] : ''} ${charCounts.future_direction[form.input.skillsheet_id] > 1000 ? sty['exceeded'] : ''}`}>
                                            {`${charCounts.future_direction[form.input.skillsheet_id] || 0} / 1000`}
                                        </div>
                                    </div>
                                    {errors[`future_direction_${form.input.skillsheet_id}`] && <p className="form__validation-msg">{errors[`future_direction_${form.input.skillsheet_id}`].message}</p>}
                                </div>
                            </div>

                            <div className={`${sty['form__flex']} ${sty['form__flex-mb-none']}`}>
                                <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                                    <div className={sty['form__label']}>プロジェクト</div>
                                </div>

                                <div className={sty['form__input']}>
                                    {skillsheetState.project_master_data.length === 0 ? (
                                        <div className={sty['form__input-project-link']}>
                                            <Link to="/mypage?tab=tab3">プロジェクト登録後に選択できます</Link>
                                        </div>
                                    ) : (
                                        skillsheetState.project_master_data.map((pmd, pmdIndex) => {
                                            // チェックボックスの状態を判定
                                            const isChecked = form.input.project?.project_data?.some(
                                                project => project.project_id === pmd.project_id.toString()
                                            ) || false;
                                            
                                            return (
                                                <div key={pmdIndex}>
                                                    <input 
                                                        type="checkbox" 
                                                        id={`p_${form.input.skillsheet_id}_${pmdIndex}`} 
                                                        name="project" 
                                                        value={`${pmd.project_id}`} 
                                                        checked={isChecked}
                                                        onChange={(e) => handleProjectCheckboxChange(e, form.input.skillsheet_id, pmd.project_id.toString())}
                                                    />
                                                    <label htmlFor={`p_${form.input.skillsheet_id}_${pmdIndex}`}>{pmd.project_name}</label>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                            <br/>

                            <div className={sty['form__flex']}>
                                <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                                    <div className={sty['form__label']}>公開設定</div>
                                    <div className={sty['required']}>必須</div>
                                </div>
                                <div className={`${sty['form__input']} ${sty['form__input-mt']}`}>
                                    <label>
                                        <input id={`is_public_on_${form.input.skillsheet_id}`}
                                            type="radio"
                                            name={`is_public_${form.input.skillsheet_id}`}
                                            checked={form.input.is_public === 'true'}
                                            value="true" onChange={(e) => setPublic(e, form.input.skillsheet_id)}/>公開
                                    </label>
                                    <label>
                                        <input id={`is_public_off_${form.input.skillsheet_id}`}
                                            type="radio"
                                            name={`is_public_${form.input.skillsheet_id}`}
                                            checked={form.input.is_public === 'false'}
                                            value="false" onChange={(e) => setPublic(e, form.input.skillsheet_id)}/>非公開
                                    </label>
                                </div>
                            </div>

                            {form.input.is_public === 'true' && form.output.random1Id !== '' && form.output.random2Id !== '' && (
                                <div className={sty['form__flex']}>
                                    <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                                        <div className={sty['form__label']}>公開URL</div>
                                    </div>
                                    <div className={`${sty['form__input']} ${sty['form__input-mt']} ${sty['form__input-lh']}`}>
                                        {form.output.random1Id !== '' && (
                                            <p id={`skillsheet_url_${form.input.skillsheet_id}`}>
                                                <a rel="noreferrer" target="_blank" href={`/${form.output.random1Id}/${form.output.random2Id}`}>
                                                    {`${process.env.REACT_APP_FRONT_BASE_URL}/${form.output.random1Id}/${form.output.random2Id}`}
                                                </a>
                                                <img className={sty['copy-icon-img']} src="/images/copy.svg" alt="コピー"
                                                onClick={() => copyUrlToClipboard(`${process.env.REACT_APP_FRONT_BASE_URL}/${form.output.random1Id}/${form.output.random2Id}`)}
                                                />
                                            </p>
                                        )}
                                        {modalOpen && (
                                            <div className={sty['modal']}>
                                                <div className={sty['modal-content']}>
                                                    コピーしました
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className={`${sty['primary-btns--skill-sheet']} ${form.output.random1Id && form.output.random2Id ? sty['has-preview'] : sty['no-preview']}`}>
                                {skillsheetState.forms.length >= 1 && (
                                    <button type="button" onClick={() => deleteForm(formIndex,form.input.skillsheet_id)} className={`${sty['delete-btn']} ${sty['btn--skill-sheet']}`}>削除</button>
                                )}
                                {form.output.random1Id && form.output.random2Id && (
                                    <div className={`${sty['preview-btn']} ${sty['btn--skill-sheet']}`}>
                                        <a href={`/preview/${form.output.random1Id}/${form.output.random2Id}`}>プレビュー</a>
                                    </div>
                                )}
                                <button
                                    type="button"
                                    className={`primary-btn ${sty['btn--skill-sheet']}`}
                                    onClick={() => submitFormHandler(form.input.skillsheet_id)}
                                    >保存</button>
                            </div>
                            <div className={sty['form__btn-left']}>
                                <button type="button" onClick={() => toggleVisibility(form.input.skillsheet_id)} className={sty['bottom-show-more-less']}>{visibleStates[form.input.skillsheet_id] ? '▲' : '▼'}</button>
                            </div>
                        </div>
                        )}
                    </div>
                </div>
            ))   
            }
        </>
    );
};


export default Skillsheet;
